import { makeAutoObservable } from "mobx";

import { ApiService } from "../api";
import UserStore from "./UserStore";
import ShareholdersStore from "./ShareholdersStore";
import { DownloadService } from "../api/DownloadService";
import { failure, ok, pending, Result } from "../common/common";

import { TMessage } from "../i18n/tt";
import { ShareType } from "../common/share";
import { StringFieldModel } from "../common/commonForms";
import AlertMessageStore from "./AlertMessageStore";

export default class ShareTypesStore {
  isLoading = false;
  error?: TMessage;
  apiService: ApiService;
  userStore: UserStore;
  shareholdersStore: ShareholdersStore;
  messageStore: AlertMessageStore;
  downloadService: DownloadService;
  deleteResult?: Result<TMessage, TMessage>;
  remarkDialogModel?: RemarkDialogModel;

  constructor(
    apiService: ApiService,
    userStore: UserStore,
    shareholdersStore: ShareholdersStore,
    downloadService: DownloadService,
    messageStore: AlertMessageStore,
  ) {
    this.apiService = apiService;
    this.userStore = userStore;
    this.shareholdersStore = shareholdersStore;
    this.downloadService = downloadService;
    this.messageStore = messageStore;
    makeAutoObservable(this, { getShareTypeById: false }, { autoBind: true });
  }

  getShareTypeById = (id: number): ShareType | undefined => {
    return this.shareholdersStore.shareTypes.find(({ sharetypeId }) => sharetypeId === id);
  };

  editShareType = async (shareType: Partial<ShareType>, shareTypeId: number) => {
    try {
      this.setLoading(true);
      await this.apiService.editShareType(
        { ...shareType, organizationnumber: this.userStore.orgNumber },
        shareTypeId,
      );
    } catch (e) {
      this.setError({ key: "shareClasses.errorStoringShareType" });
      throw e;
    } finally {
      this.setLoading(false);
    }
  };

  createShareType = async (shareType: Partial<ShareType>) => {
    try {
      this.setLoading(true);
      await this.apiService.createShareType({
        ...shareType,
        organizationnumber: this.userStore.orgNumber,
      });
    } catch (e) {
      this.setError({ key: "shareClasses.errorStoringShareType" });
      throw e;
    } finally {
      this.setLoading(false);
    }
  };

  *deleteShareType(shareTypeId: number, onOk: () => void) {
    try {
      this.deleteResult = pending();
      yield this.apiService.deleteShareType(shareTypeId, this.userStore.orgNumber);
      this.deleteResult = ok({ key: "ok" });
      onOk();
    } catch (e) {
      this.deleteResult = failure({ key: "deleting_failed" });
    }
  }

  resetDeleteResult() {
    this.deleteResult = undefined;
  }

  setError(error: TMessage | undefined) {
    this.error = error;
  }
  private setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  showRemarksDialog() {
    this.remarkDialogModel = new RemarkDialogModel(this.userStore.companyInfo?.remark);
  }

  cancelRemarksDialog() {
    this.remarkDialogModel = undefined;
  }

  *submitRemarksDialog() {
    try {
      const remark = this.remarkDialogModel?.remarkText.inputValue;
      yield this.apiService.updateCompanyRemark(this.userStore.orgNumber, remark);
      this.remarkDialogModel = undefined;

      if (this.userStore.companyInfo) {
        this.userStore.companyInfo.remark = remark;
      }
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }
}

class RemarkDialogModel {
  remarkText: StringFieldModel;

  constructor(remark: string | undefined) {
    this.remarkText = new StringFieldModel({ value: remark });
  }
}
